/* eslint-disable react-refresh/only-export-components -- bewusst co-lozierter Provider+Hook (analog LegalToggleContext); react-refresh ist eine reine Dev-HMR-Optimierung. */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  parseDismissedVideoHints,
  parseVideoHintsEnabled,
  VIDEO_HINTS_DISMISSED_KEY,
  VIDEO_HINTS_ENABLED_KEY,
} from './video-hint-visibility';

interface VideoHintApi {
  /** Globaler Schalter aus den Einstellungen (Default: an). */
  showVideoHints: boolean;
  setShowVideoHints: (value: boolean) => void;
  /** Einzeln weggeklickte Video-Slugs. */
  dismissed: ReadonlySet<string>;
  dismiss: (slug: string) => void;
}

const VideoHintContext = createContext<VideoHintApi | null>(null);

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode etc. — Einstellung bleibt für die Session im State. */
  }
}

/**
 * Hält die Sichtbarkeit der Video-Hinweise — analog `LegalToggleProvider` für
 * die Rechtshinweise: Zustand im Context, Persistenz in localStorage.
 *
 * Der Zustand liegt bewusst zentral und nicht je `<VideoHint>`-Instanz, damit
 * ein weggeklickter Hinweis sofort überall verschwindet.
 */
export function VideoHintProvider({ children }: { children: ReactNode }) {
  const [showVideoHints, setShowVideoHintsState] = useState<boolean>(() =>
    parseVideoHintsEnabled(read(VIDEO_HINTS_ENABLED_KEY)),
  );
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() =>
    parseDismissedVideoHints(read(VIDEO_HINTS_DISMISSED_KEY)),
  );

  const setShowVideoHints = useCallback((value: boolean) => {
    setShowVideoHintsState(value);
    write(VIDEO_HINTS_ENABLED_KEY, String(value));
    if (value) {
      // Wieder einschalten heißt „ich will die Hinweise sehen" — sonst bliebe
      // der Schalter wirkungslos, solange alle Hinweise weggeklickt sind.
      setDismissed(new Set());
      write(VIDEO_HINTS_DISMISSED_KEY, '[]');
    }
  }, []);

  const dismiss = useCallback((slug: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(slug);
      write(VIDEO_HINTS_DISMISSED_KEY, JSON.stringify(Array.from(next)));
      return next;
    });
  }, []);

  const value = useMemo<VideoHintApi>(
    () => ({ showVideoHints, setShowVideoHints, dismissed, dismiss }),
    [showVideoHints, setShowVideoHints, dismissed, dismiss],
  );

  return <VideoHintContext.Provider value={value}>{children}</VideoHintContext.Provider>;
}

export function useVideoHints(): VideoHintApi {
  const ctx = useContext(VideoHintContext);
  if (!ctx) throw new Error('useVideoHints must be used inside <VideoHintProvider>');
  return ctx;
}
